import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_ORDER = {
  supplierId: '',
  purchaseDate: today(),
  invoiceNo: '',
  taxAmount: '0',
  notes: '',
};

const EMPTY_SUPPLIER = {
  name: '',
  phone: '',
  email: '',
  address: '',
  gstNo: '',
  contactPerson: '',
  notes: '',
};

const EMPTY_ITEM = { itemName: '', quantity: '', unit: '', rate: '', inventoryItemId: null };

const inp = { className: 'form-control' };

function F({ label, children, col }) {
  return (
    <div className="form-group" style={col ? { gridColumn: col } : {}}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)',
        width: '100%', maxWidth: wide ? 860 : 520, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid #e8eaed',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>{title}</div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368', lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

function PayBadge({ status }) {
  const cfg = {
    pending: { bg: '#fff3e0', color: '#e65100' },
    partial: { bg: '#e3f2fd', color: '#1565c0' },
    paid:    { bg: '#e6f4ea', color: '#27ae60' },
  };
  const s = cfg[status] || cfg.pending;
  return (
    <span style={{
      background: s.bg, color: s.color,
      borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700,
    }}>{status}</span>
  );
}

function OrderRow({ order, onMarkPaid, onDelete, role }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const expand = async () => {
    if (!expanded && items.length === 0) {
      setLoadingItems(true);
      const d = await hospitalApi.getPurchaseOrder(order.id);
      if (d && d.items) setItems(d.items);
      setLoadingItems(false);
    }
    setExpanded(!expanded);
  };

  return (
    <>
      <tr
        onClick={expand}
        style={{
          borderTop: '1px solid #f1f3f4',
          background: expanded ? LIGHT : 'inherit',
          cursor: 'pointer',
        }}
      >
        <td style={{ padding: '10px 12px', color: '#5f6368' }}>{order.purchase_date}</td>
        <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>{order.invoice_no || '—'}</td>
        <td style={{ padding: '10px 12px', color: '#202124' }}>{order.supplier_name || '—'}</td>
        <td style={{ padding: '10px 12px', textAlign: 'center', color: '#5f6368' }}>{order.item_count || 0}</td>
        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#5f6368' }}>{fmtRs(order.subtotal)}</td>
        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#5f6368' }}>{fmtRs(order.tax_amount)}</td>
        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: DARK }}>{fmtRs(order.total)}</td>
        <td style={{ padding: '10px 12px' }}><PayBadge status={order.payment_status} /></td>
        <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', gap: 6 }}>
            {order.payment_status !== 'paid' && (
              <button
                style={{ fontSize: 11, padding: '3px 8px', background: '#e6f4ea',
                  color: '#27ae60', border: '1px solid #a8d5b5', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => onMarkPaid(order)}
              >Mark Paid</button>
            )}
            {role === 'admin' && (
              <button
                style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                  color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => onDelete(order.id)}
              >Delete</button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: '#f8fbff' }}>
          <td colSpan={9} style={{ padding: '0 12px 12px 32px' }}>
            {loadingItems ? (
              <div style={{ color: '#9aa0a6', fontSize: 13, padding: '8px 0' }}>Loading items…</div>
            ) : items.length === 0 ? (
              <div style={{ color: '#9aa0a6', fontSize: 13, padding: '8px 0' }}>No items found</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                <thead>
                  <tr style={{ background: LIGHT }}>
                    {['Item Name', 'Qty', 'Unit', 'Rate', 'Total'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: DARK, fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid #eef0f2' }}>
                      <td style={{ padding: '6px 10px', color: '#202124' }}>{it.item_name}</td>
                      <td style={{ padding: '6px 10px', color: '#5f6368' }}>{Number(it.quantity)}</td>
                      <td style={{ padding: '6px 10px', color: '#5f6368' }}>{it.unit || '—'}</td>
                      <td style={{ padding: '6px 10px', color: '#5f6368' }}>{fmtRs(it.rate)}</td>
                      <td style={{ padding: '6px 10px', fontWeight: 600, color: DARK }}>{fmtRs(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {order.notes && (
              <div style={{ fontSize: 12, color: '#5f6368', marginTop: 8 }}>Notes: {order.notes}</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function PurchaseOrdersTab({ role }) {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_ORDER);
  const [lineItems, setLineItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    hospitalApi.getPurchaseOrders().then(d => { if (d) setOrders(d); });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    hospitalApi.getSuppliers().then(d => { if (d) setSuppliers(d); });
  }, []);

  const subtotal = lineItems.reduce((s, it) => {
    return s + (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
  }, 0);
  const taxAmt = parseFloat(form.taxAmount) || 0;
  const grandTotal = subtotal + taxAmt;

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_ITEM }]);

  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const updateLine = (idx, field, value) => {
    setLineItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const openModal = () => {
    setForm(EMPTY_ORDER);
    setLineItems([{ ...EMPTY_ITEM }]);
    setErr('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.supplierId) { setErr('Supplier is required'); return; }
    const validItems = lineItems.filter(it => it.itemName.trim());
    if (validItems.length === 0) { setErr('Add at least one item'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        supplierId: Number(form.supplierId),
        purchaseDate: form.purchaseDate || today(),
        invoiceNo: form.invoiceNo,
        taxAmount: parseFloat(form.taxAmount) || 0,
        notes: form.notes,
        items: validItems.map(it => ({
          itemName: it.itemName,
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit,
          rate: parseFloat(it.rate) || 0,
          total: (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0),
          inventoryItemId: null,
        })),
      };
      await hospitalApi.createPurchaseOrder(payload);
      load();
      setShowModal(false);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleMarkPaid = async (order) => {
    if (!window.confirm('Mark this order as paid?')) return;
    await hospitalApi.updatePurchasePayment(order.id, {
      paymentStatus: 'paid',
      paidAmount: parseFloat(order.total) || 0,
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this purchase order?')) return;
    await hospitalApi.deletePurchaseOrder(id);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {role !== 'viewer' && (
          <button className="btn btn-primary" onClick={openModal}>+ New Purchase</button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {orders.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>No purchase orders found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Date', 'Invoice No', 'Supplier', 'Items', 'Subtotal', 'Tax', 'Total', 'Payment Status', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '10px 12px', textAlign: h === 'Items' ? 'center' : ['Subtotal', 'Tax', 'Total'].includes(h) ? 'right' : 'left',
                      color: DARK, fontWeight: 700, fontSize: 12,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <OrderRow
                    key={o.id}
                    order={{ ...o, _even: i % 2 === 0 }}
                    onMarkPaid={handleMarkPaid}
                    onDelete={handleDelete}
                    role={role}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <Modal title="New Purchase Order" onClose={() => setShowModal(false)} wide>
          {err && (
            <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
              {err}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            <F label="Supplier *">
              <select {...inp} value={form.supplierId} onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))}>
                <option value="">Select Supplier</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </F>
            <F label="Purchase Date">
              <input {...inp} type="date" value={form.purchaseDate}
                onChange={e => setForm(f => ({ ...f, purchaseDate: e.target.value }))} />
            </F>
            <F label="Invoice No">
              <input {...inp} value={form.invoiceNo} placeholder="INV-001"
                onChange={e => setForm(f => ({ ...f, invoiceNo: e.target.value }))} />
            </F>
            <F label="Tax Amount (₹)">
              <input {...inp} type="number" min={0} value={form.taxAmount}
                onChange={e => setForm(f => ({ ...f, taxAmount: e.target.value }))} />
            </F>
            <F label="Notes" col="1/-1">
              <textarea {...inp} rows={2} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </F>
          </div>

          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: DARK, fontSize: 14 }}>Line Items</div>
              <button
                style={{ fontSize: 12, padding: '4px 12px', background: LIGHT, color: DARK,
                  border: `1px solid ${C}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                onClick={addLine}>+ Add Item</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Item Name', 'Quantity', 'Unit', 'Rate (₹)', 'Total', ''].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((it, idx) => {
                  const rowTotal = (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
                  return (
                    <tr key={idx} style={{ borderTop: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '6px 6px' }}>
                        <input {...inp} value={it.itemName} placeholder="Item name"
                          onChange={e => updateLine(idx, 'itemName', e.target.value)}
                          style={{ fontSize: 12 }} />
                      </td>
                      <td style={{ padding: '6px 6px', width: 90 }}>
                        <input {...inp} type="number" min={0} value={it.quantity} placeholder="0"
                          onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          style={{ fontSize: 12 }} />
                      </td>
                      <td style={{ padding: '6px 6px', width: 80 }}>
                        <input {...inp} value={it.unit} placeholder="pcs"
                          onChange={e => updateLine(idx, 'unit', e.target.value)}
                          style={{ fontSize: 12 }} />
                      </td>
                      <td style={{ padding: '6px 6px', width: 110 }}>
                        <input {...inp} type="number" min={0} value={it.rate} placeholder="0.00"
                          onChange={e => updateLine(idx, 'rate', e.target.value)}
                          style={{ fontSize: 12 }} />
                      </td>
                      <td style={{ padding: '6px 10px', fontWeight: 600, color: DARK, whiteSpace: 'nowrap' }}>
                        {fmtRs(rowTotal)}
                      </td>
                      <td style={{ padding: '6px 6px', width: 36 }}>
                        {lineItems.length > 1 && (
                          <button
                            onClick={() => removeLine(idx)}
                            style={{ background: '#fce8e6', border: '1px solid #f5c6c6', borderRadius: 4,
                              color: '#c5221f', cursor: 'pointer', fontSize: 14, padding: '2px 7px', fontWeight: 700 }}>
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, padding: 14, background: LIGHT, borderRadius: 8, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, color: '#5f6368' }}>Subtotal</div>
              <div style={{ fontWeight: 700, color: DARK, fontSize: 15 }}>{fmtRs(subtotal)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#5f6368' }}>Tax</div>
              <div style={{ fontWeight: 700, color: '#e67e22', fontSize: 15 }}>{fmtRs(taxAmt)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#5f6368' }}>Grand Total</div>
              <div style={{ fontWeight: 800, color: '#27ae60', fontSize: 18 }}>{fmtRs(grandTotal)}</div>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Purchase Order'}
            </button>
            <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
              background: '#fff', cursor: 'pointer', fontSize: 13 }}
              onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SuppliersTab({ role }) {
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    hospitalApi.getSuppliers().then(d => { if (d) setSuppliers(d); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY_SUPPLIER);
    setEditId(null);
    setErr('');
    setShowModal(true);
  };

  const openEdit = (s) => {
    setForm({
      name: s.name || '',
      phone: s.phone || '',
      email: s.email || '',
      address: s.address || '',
      gstNo: s.gst_no || '',
      contactPerson: s.contact_person || '',
      notes: s.notes || '',
    });
    setEditId(s.id);
    setErr('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await hospitalApi.updateSupplier(editId, form);
      else await hospitalApi.createSupplier(form);
      load();
      setShowModal(false);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    await hospitalApi.deleteSupplier(id);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        {role !== 'viewer' && (
          <button className="btn btn-primary" onClick={openAdd}>+ Add Supplier</button>
        )}
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {suppliers.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>No suppliers found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Name', 'Phone', 'Email', 'Contact Person', 'GST No', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s, i) => (
                  <tr key={s.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>{s.name}</td>
                    <td style={{ padding: '10px 12px', color: '#5f6368' }}>{s.phone || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#5f6368', fontSize: 12 }}>{s.email || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#5f6368' }}>{s.contact_person || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#5f6368' }}>{s.gst_no || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {role !== 'viewer' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                              color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => openEdit(s)}>Edit</button>
                          {role === 'admin' && (
                            <button
                              style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                                color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => handleDelete(s.id)}>Delete</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit Supplier' : 'Add Supplier'} onClose={() => setShowModal(false)}>
          {err && (
            <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
              {err}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            <F label="Name *">
              <input {...inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </F>
            <F label="Phone">
              <input {...inp} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </F>
            <F label="Email">
              <input {...inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </F>
            <F label="Contact Person">
              <input {...inp} value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} />
            </F>
            <F label="GST No">
              <input {...inp} value={form.gstNo} onChange={e => setForm(f => ({ ...f, gstNo: e.target.value }))} />
            </F>
            <F label="Address" col="1/-1">
              <textarea {...inp} rows={2} value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </F>
            <F label="Notes" col="1/-1">
              <textarea {...inp} rows={2} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </F>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update Supplier' : 'Add Supplier'}
            </button>
            <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
              background: '#fff', cursor: 'pointer', fontSize: 13 }}
              onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function HospitalPurchases({ role }) {
  const [tab, setTab] = useState('orders');

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'orders', label: 'Purchase Orders' },
          { key: 'suppliers', label: 'Suppliers' },
        ].map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'orders' && <PurchaseOrdersTab role={role} />}
      {tab === 'suppliers' && <SuppliersTab role={role} />}
    </div>
  );
}
